import React, { useMemo, useState } from 'react';
import type { VcsConnection } from './vcs-connection-modal-picker';
import { FormattedMessage } from 'react-intl';
import { Col, Row } from 'components/common/grid';
import { VcsConnectionModalFormAuthenticationTypeButton } from './vcs-connection-modal-form-authentication-type-button';
import { VcsConnectionModalButtons } from './vcs-connection-modal-buttons';
import { VcsConnectionModalFormOauth } from './vcs-connection-modal-form-oauth';
import { VcsConnectionModalFormAccessToken } from './vcs-connection-modal-form-access-token';
import { VcsConnectionModalFormVcsAgent } from './vcs-connection-modal-form-vcs-agent';
import styled from '@emotion/styled';
import type { AuthenticationType, VcsConnectionsForm } from './access-card/use-connection-modal-access-card-form.hook';
import { useVcsConnectionModalForm } from './access-card/use-connection-modal-access-card-form.hook';
import {
  getAvailableAuthenticationTypes,
  getAzureDevOpsAuthenticationTypeInUse
} from 'components/organizations/vcs/vcs-connection-modal/access-card/supported-vcs-connections-common';
import { Tag } from 'components/common/tag';
import { BridgedAlert as Alert } from 'components/common/feedback/bridged-alert';

function getModalForm(authenticationType: AuthenticationType): React.FunctionComponent<VcsConnectionsForm> {
  switch (authenticationType) {
    case 'OAuth':
    case 'EntraOAuth':
      return VcsConnectionModalFormOauth;
    case 'AccessToken':
      return VcsConnectionModalFormAccessToken;
    case 'VcsAgent':
      return VcsConnectionModalFormVcsAgent;
  }
}

export const VcsConnectionsModalConnectionDetails: React.FC<{
  vcsConnection: VcsConnection;
  onSubmit: (data: any) => void;
  hideModal: () => void;
}> = ({ vcsConnection, onSubmit, hideModal }) => {
  const { form } = useVcsConnectionModalForm({ vcsConnection, onSubmit });
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const authenticationTypeInUse = getAzureDevOpsAuthenticationTypeInUse(vcsConnection);
  const authenticationOptions = useMemo(() => {
    const authenticationTypes = getAvailableAuthenticationTypes(vcsConnection);
    const labelPrefix = vcsConnection.type === 'AzureDevOps' ? 'AzureDevOps.' : '';
    return authenticationTypes.map((type: AuthenticationType) => ({
      label: (
        <span data-e2e={`configure-authentication-type-${type}-picker`}>
          <FormattedMessage
            id={`organization.settings.agents.vcs-connections-modal.authentication.${labelPrefix}${type}`}
          />
          {type === authenticationTypeInUse && (
            <InUseTag color="green" newDesignPreset="green" data-e2e="authentication-type-in-use">
              <FormattedMessage id="organization.settings.agents.vcs-connections-modal.authentication.in-use" />
            </InUseTag>
          )}
        </span>
      ),
      value: type,
      disabled: authenticationTypes.length === 1
    }));
  }, [vcsConnection, authenticationTypeInUse]);

  const authenticationType = form.watch('authenticationType');

  const ModalForm = useMemo(() => getModalForm(authenticationType), [authenticationType]);
  const authenticationTypeButton = (
    <VcsConnectionModalFormAuthenticationTypeButton options={authenticationOptions} form={form} />
  );

  return (
    <>
      {authenticationTypeInUse === 'OAuth' && (
        <Row style={{ marginBottom: '16px' }}>
          <Alert
            type="warning"
            message={
              <FormattedMessage id="organization.settings.agents.vcs-connections-modal.authentication.AzureDevOps.retiring" />
            }
          />
        </Row>
      )}
      <Row style={{ marginTop: '-8px' }}>
        {/* Full width so the longer Azure DevOps labels do not wrap inside the equal-width buttons */}
        {vcsConnection.type === 'AzureDevOps' ? (
          <Col span={24}>{authenticationTypeButton}</Col>
        ) : (
          authenticationTypeButton
        )}
      </Row>
      <FormContainer>
        <ModalForm
          key={authenticationType}
          vcsConnection={vcsConnection}
          form={form}
          onTokenProcessingChange={setIsProcessingToken}
        />
      </FormContainer>
      <VcsConnectionModalButtons
        form={form}
        vcsConnection={vcsConnection}
        onSave={hideModal}
        isPending={isProcessingToken}
      />
    </>
  );
};

const InUseTag = styled(Tag)`
  margin-inline: 6px 0;
  vertical-align: middle;
`;

const FormContainer = styled.div`
  padding: 20px 0;
`;
